// src/components/Colaborador/PlanoDesenvolvimentoIndividual.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebaseConfig';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import './PDIModal.css'; // Certifique-se que este arquivo CSS existe e está estilizado

// Componente de Paginação
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }
  return (
    <nav>
      <ul className="pagination-wrapper pdi-modal-pagination">
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            Anterior
          </button>
        </li>
        {pageNumbers.map(number => (
          <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
            <button className="page-link" onClick={() => onPageChange(number)}>
              {number}
            </button>
          </li>
        ))}
        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Próxima
          </button>
        </li>
      </ul>
    </nav>
  );
};

// Função para obter o status de exibição da meta
const getMetaDisplayStatus = (meta) => {
    const { status, prazo } = meta;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let prazoDateObj;
    if (prazo) {
        if (prazo.seconds) { // Firestore Timestamp
            prazoDateObj = new Date(prazo.seconds * 1000 + (prazo.nanoseconds || 0) / 1000000);
        } else if (typeof prazo === 'string') { // YYYY-MM-DD string
            const parts = prazo.split('-');
            if (parts.length === 3) { // Especificamente YYYY-MM-DD
                const utcDate = new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])));
                prazoDateObj = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate()); // Cria como data local sem fuso
            } else { // Tenta parse genérico para outros formatos de string
                try { prazoDateObj = new Date(prazo); } catch (e) { /* ignora erro de parse */ }
            }
        } else if (prazo instanceof Date) { // Já é um objeto Date
            prazoDateObj = new Date(prazo.getFullYear(), prazo.getMonth(), prazo.getDate());
        }
    }

    if (prazoDateObj && !isNaN(prazoDateObj.getTime())) {
        prazoDateObj.setHours(0,0,0,0); // Normaliza para meia-noite para comparações de data
    } else { // Se não há prazo ou o prazo é inválido
        const baseStatus = status || 'Não Iniciado';
        return { text: baseStatus, classNameSuffix: baseStatus.toLowerCase().replace(/\s+/g, '-') || 'nao-iniciado', daysOverdue: 0 };
    }

    if (status === 'Concluído') {
        return { text: 'Concluído', classNameSuffix: 'concluido', daysOverdue: 0 };
    }

    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const todayDay = today.getDate();

    const prazoYear = prazoDateObj.getFullYear();
    const prazoMonth = prazoDateObj.getMonth();
    const prazoDay = prazoDateObj.getDate();

    if (todayYear > prazoYear ||
        (todayYear === prazoYear && todayMonth > prazoMonth) ||
        (todayYear === prazoYear && todayMonth === prazoMonth && todayDay > prazoDay)
    ) {
        const diffTime = new Date(todayYear, todayMonth, todayDay).getTime() - new Date(prazoYear, prazoMonth, prazoDay).getTime();
        const diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
        if (diffDays > 0) {
             return { text: `Em Atraso (${diffDays} dia${diffDays > 1 ? 's' : ''})`, classNameSuffix: 'em-atraso', daysOverdue: diffDays };
        }
    }
    const baseStatus = status || 'Não Iniciado';
    return { text: baseStatus, classNameSuffix: baseStatus.toLowerCase().replace(/\s+/g, '-') || 'nao-iniciado', daysOverdue: 0 };
};

// Padroniza item de feedback
const standardizeFeedbackItem = (fb) => {
    let jsDate = null;
    if (fb.data?.seconds) { // Firestore Timestamp
        jsDate = new Date(fb.data.seconds * 1000 + (fb.data.nanoseconds || 0) / 1000000);
    } else if (fb.data instanceof Date) { // Já é Date
        jsDate = fb.data;
    } else if (typeof fb.data === 'string') { // String de data
        const parsed = new Date(fb.data); // Tenta parsear
        if (!isNaN(parsed.getTime())) { // Verifica se o parse foi válido
            jsDate = parsed;
        }
    }
    return {
        ...fb,
        id: fb.id || uuidv4(), // Garante um ID
        data: jsDate instanceof Date && !isNaN(jsDate.getTime()) ? jsDate : null // Garante que 'data' seja Date ou null
    };
};

// Padroniza item de meta
const standardizeMetaItem = (meta) => {
    let prazoFinal = meta.prazo;
    if (meta.prazo && meta.prazo.seconds) { // Se for Timestamp do Firestore
        prazoFinal = new Date(meta.prazo.seconds * 1000 + (meta.prazo.nanoseconds || 0) / 1000000);
    } else if (meta.prazo && typeof meta.prazo === 'string' && !meta.prazo.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Se for string mas não no formato YYYY-MM-DD, tenta converter para Date
        const parsedDate = new Date(meta.prazo);
        if (!isNaN(parsedDate.getTime())) prazoFinal = parsedDate;
    } // Se for string YYYY-MM-DD ou Date, mantém como está.

    return {
        id: meta.id || uuidv4(),
        descricao: meta.descricao || "",
        prazo: prazoFinal, // Pode ser Date object ou string YYYY-MM-DD
        status: meta.status || "Não Iniciado",
        observacoes: meta.observacoes || ""
    };
};


const PlanoDesenvolvimentoIndividual = ({ pdiData: initialPdiData, userId, userName, onPDIUpdate }) => {
  const [pdiData, setPdiData] = useState({ objetivoPrincipal: '', metas: [], feedbackContinua: [] });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalObjetivoPrincipal, setModalObjetivoPrincipal] = useState('');
  const [objetivoDebounceTimeout, setObjetivoDebounceTimeout] = useState(null);

  const [modalMetas, setModalMetas] = useState([]);
  const [editingMeta, setEditingMeta] = useState(null);
  const [novaMetaForm, setNovaMetaForm] = useState({ descricao: '', prazo: '', status: 'Não Iniciado', observacoes: '' });

  const [modalFeedbacks, setModalFeedbacks] = useState([]);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [novoFeedbackText, setNovoFeedbackText] = useState('');

  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState('');

  const METAS_PER_PAGE = 2;
  const FEEDBACKS_PER_PAGE = 3;
  const [currentPageMetasMain, setCurrentPageMetasMain] = useState(1);
  const [currentPageFeedbacksModal, setCurrentPageFeedbacksModal] = useState(1);

  const clearMessages = useCallback(() => { setMensagem(''); setErro(''); }, []);
  const showSuccess = useCallback((msg, duration = 3000) => { setErro(''); setMensagem(msg); setTimeout(clearMessages, duration); }, [clearMessages]);
  const showError = useCallback((msg, duration = 5000) => { setMensagem(''); setErro(msg); setTimeout(clearMessages, duration); }, [clearMessages]);

  const processPdiData = useCallback((data) => {
    const feedbacksRaw = Array.isArray(data?.feedbackContinua) ? data.feedbackContinua : [];
    const metasRaw = Array.isArray(data?.metas) ? data.metas : [];
    const processedFeedbacks = feedbacksRaw.map(standardizeFeedbackItem).filter(fb => fb.data instanceof Date && !isNaN(fb.data.getTime())).sort((a, b) => b.data.getTime() - a.data.getTime());
    const processedMetas = metasRaw.map(standardizeMetaItem);
    return {
      objetivoPrincipal: data?.objetivoPrincipal || '',
      metas: processedMetas,
      feedbackContinua: processedFeedbacks,
      ultimaAtualizacao: data?.ultimaAtualizacao,
      ultimaAtualizacaoFeedbacks: data?.ultimaAtualizacaoFeedbacks
    };
  }, []);

  useEffect(() => {
    const processedData = processPdiData(initialPdiData);
    setPdiData(processedData);
    if (!isModalOpen) {
        setModalObjetivoPrincipal(processedData.objetivoPrincipal);
        setModalMetas(processedData.metas.map(meta => ({...meta, prazo: formatDateForInput(meta.prazo) })));
        setModalFeedbacks([...processedData.feedbackContinua]);
    }
  }, [initialPdiData, isModalOpen, processPdiData]);

  const formatDateForInput = (dateSource) => {
    if (!dateSource) return '';
    try {
      let date;
      if (dateSource.seconds) {
        date = new Date(dateSource.seconds * 1000 + (dateSource.nanoseconds || 0) / 1000000);
      } else if (dateSource instanceof Date) {
        date = dateSource;
      } else if (typeof dateSource === 'string') {
        if (dateSource.match(/^\d{4}-\d{2}-\d{2}$/)) return dateSource;
        date = new Date(dateSource);
      } else { return ''; }
      if (isNaN(date.getTime())) return '';
      const year = date.getUTCFullYear();
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = date.getUTCDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) { return ''; }
  };

  const formatDisplayDate = (dateSource) => {
    if (!dateSource) return 'N/D';
    try {
        let date;
        if (dateSource?.seconds !== undefined) {
            date = new Date(dateSource.seconds * 1000 + (dateSource.nanoseconds || 0) / 1000000);
        } else if (dateSource instanceof Date) {
            date = dateSource;
        } else if (typeof dateSource === 'string') {
            date = new Date(dateSource.includes('T') ? dateSource : dateSource + 'T00:00:00');
        } else { return 'Data inválida (tipo)'; }
        if (isNaN(date.getTime())) return 'Data inválida (parse)';
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) { return 'Data inválida (erro)'; }
  };

  const persistObjetivoPrincipal = useCallback(async (objetivoParaSalvar, currentUserId) => {
    if (currentUserId && objetivoParaSalvar !== undefined) {
        try {
            const userPrivateDocRef = doc(db, 'usersprivado', currentUserId);
            const updateData = { "pdi.objetivoPrincipal": objetivoParaSalvar, "pdi.ultimaAtualizacao": new Date() };
            await updateDoc(userPrivateDocRef, updateData);
            setPdiData(prevPdiData => ({ ...prevPdiData, objetivoPrincipal: objetivoParaSalvar, ultimaAtualizacao: updateData["pdi.ultimaAtualizacao"] }));
            return true;
        } catch (error) {
            console.error("Erro ao persistir objetivo principal:", error); showError(`Erro ao salvar objetivo: ${error.message}. Verifique permissões para 'usersprivado'.`);
            throw error;
        }
    }
    return false;
  }, [showError, setPdiData]);

  // Auto-save para Objetivo Principal com debounce CORRIGIDO
  useEffect(() => {
    if (!isModalOpen || !userId || modalObjetivoPrincipal === undefined) return;
    if (modalObjetivoPrincipal === pdiData.objetivoPrincipal) return;

    if (objetivoDebounceTimeout) {
        clearTimeout(objetivoDebounceTimeout);
    }
    const newTimeout = setTimeout(async () => {
        try {
            await persistObjetivoPrincipal(modalObjetivoPrincipal, userId);
            showSuccess("Objetivo salvo automaticamente!", 1500);
        } catch (error) { /* Erro já tratado e exibido por persistObjetivoPrincipal */ }
    }, 1500);
    setObjetivoDebounceTimeout(newTimeout);
    return () => {
        clearTimeout(newTimeout);
    };
  }, [modalObjetivoPrincipal, isModalOpen, userId, pdiData.objetivoPrincipal, persistObjetivoPrincipal, showSuccess]); // REMOVIDO 'objetivoDebounceTimeout'

  const openPDIModal = () => {
    setModalObjetivoPrincipal(pdiData.objetivoPrincipal);
    setModalMetas(pdiData.metas.map(meta => ({...meta, prazo: formatDateForInput(meta.prazo) })));
    setModalFeedbacks([...pdiData.feedbackContinua]);
    setCurrentPageMetasMain(1);
    setCurrentPageFeedbacksModal(1);
    setEditingMeta(null);
    setNovaMetaForm({ descricao: '', prazo: '', status: 'Não Iniciado', observacoes: '' });
    setNovoFeedbackText('');
    setEditingFeedback(null);
    clearMessages();
    setIsModalOpen(true);
  };

  const closePDIModal = async () => {
    if (objetivoDebounceTimeout) { clearTimeout(objetivoDebounceTimeout); setObjetivoDebounceTimeout(null); }
    let objectiveChangedAndNeedsSave = isModalOpen && userId && modalObjetivoPrincipal !== undefined && modalObjetivoPrincipal !== pdiData.objetivoPrincipal;
    if (objectiveChangedAndNeedsSave) {
        showSuccess("Salvando alterações finais no objetivo...", 2000);
        try { await persistObjetivoPrincipal(modalObjetivoPrincipal, userId); } catch (error) { /* Erro já tratado */ }
    }
    setIsModalOpen(false);
    if (onPDIUpdate) onPDIUpdate();
  };

  const getStandardizedDataFromDb = async (userPrivateDocRef, fieldName, itemStandardizer) => {
    const userDocSnap = await getDoc(userPrivateDocRef);
    const currentPdiFromDb = userDocSnap.data()?.pdi || { [fieldName]: [] };
    const rawItems = Array.isArray(currentPdiFromDb[fieldName]) ? currentPdiFromDb[fieldName] : [];
    return rawItems.map(itemStandardizer);
  };

  // DEFINIÇÃO DE handleNovaMetaFormChange
  const handleNovaMetaFormChange = (e) => {
    const { name, value } = e.target;
    setNovaMetaForm(prev => ({ ...prev, [name]: value }));
  };

  const adicionarNovaMetaModal = async () => {
    if (!novaMetaForm.descricao || !novaMetaForm.prazo) { showError("Descrição e prazo da nova meta são obrigatórios."); return; }
    clearMessages();
    const novaMetaComIdEPadronizada = standardizeMetaItem({ ...novaMetaForm });
    const originalPdiMetas = pdiData.metas;
    const originalModalMetas = modalMetas;

    const sortedNewPdiMetas = [...originalPdiMetas, novaMetaComIdEPadronizada].sort((a,b) => (formatDateForInput(a.prazo) || "").localeCompare(formatDateForInput(b.prazo) || ""));
    setPdiData(prev => ({ ...prev, metas: sortedNewPdiMetas, ultimaAtualizacao: new Date() }));
    setModalMetas(prev => [...prev, { ...novaMetaComIdEPadronizada, prazo: formatDateForInput(novaMetaComIdEPadronizada.prazo) }].sort((a,b) => (a.prazo || "").localeCompare(b.prazo || "")));
    setNovaMetaForm({ descricao: '', prazo: '', status: 'Não Iniciado', observacoes: '' });

    try {
      if (!userId) throw new Error("ID do usuário não encontrado.");
      const userPrivateDocRef = doc(db, 'usersprivado', userId);
      const metasFromDb = await getStandardizedDataFromDb(userPrivateDocRef, 'metas', standardizeMetaItem);
      const finalMetasParaDb = [...metasFromDb, novaMetaComIdEPadronizada].sort((a,b) => (formatDateForInput(a.prazo) || "").localeCompare(formatDateForInput(b.prazo) || ""));
      await updateDoc(userPrivateDocRef, { "pdi.metas": finalMetasParaDb, "pdi.ultimaAtualizacao": new Date() });
      showSuccess("Nova meta adicionada e salva no PDI!");
    } catch (error) {
      console.error("Erro ao adicionar nova meta:", error); showError(`Erro ao salvar meta: ${error.message}. Verifique permissões.`);
      setPdiData(prev => ({ ...prev, metas: originalPdiMetas })); setModalMetas(originalModalMetas);
    }
  };

  const startEditMeta = (metaToEdit) => { setEditingMeta(standardizeMetaItem({...metaToEdit, prazo: formatDateForInput(metaToEdit.prazo)})); };
  const handleEditingMetaChange = (e) => { const { name, value } = e.target; setEditingMeta(prev => standardizeMetaItem({ ...prev, [name]: value })); };
  const cancelEditMeta = () => { setEditingMeta(null); };

  const saveEditedMeta = async () => {
    if (!editingMeta || !editingMeta.descricao || !editingMeta.prazo) { showError("Descrição e prazo da meta são obrigatórios."); return; }
    clearMessages();
    const metaAtualizada = standardizeMetaItem(editingMeta);
    const originalPdiMetas = pdiData.metas; const originalModalMetas = modalMetas;

    setPdiData(prev => ({...prev, metas: prev.metas.map(m => m.id === metaAtualizada.id ? metaAtualizada : m).sort((a,b) => (formatDateForInput(a.prazo) || "").localeCompare(formatDateForInput(b.prazo) || "")), ultimaAtualizacao: new Date()}));
    setModalMetas(prev => prev.map(m => m.id === metaAtualizada.id ? {...metaAtualizada, prazo: formatDateForInput(metaAtualizada.prazo)} : m ).sort((a,b) => (a.prazo || "").localeCompare(b.prazo || "")));
    setEditingMeta(null);

    try {
        if (!userId) throw new Error("ID do usuário não encontrado.");
        const userPrivateDocRef = doc(db, 'usersprivado', userId);
        const metasFromDb = await getStandardizedDataFromDb(userPrivateDocRef, 'metas', standardizeMetaItem);
        const finalMetasParaDb = metasFromDb.map(m => m.id === metaAtualizada.id ? metaAtualizada : m).sort((a,b) => (formatDateForInput(a.prazo) || "").localeCompare(formatDateForInput(b.prazo) || ""));
        await updateDoc(userPrivateDocRef, { "pdi.metas": finalMetasParaDb, "pdi.ultimaAtualizacao": new Date() });
        showSuccess("Meta atualizada e salva no PDI!");
    } catch (error) {
        console.error("Erro ao salvar meta editada:", error); showError(`Erro ao salvar meta: ${error.message}. Verifique permissões.`);
        setPdiData(prev => ({ ...prev, metas: originalPdiMetas })); setModalMetas(originalModalMetas);
    }
  };

  const removerMetaModal = async (idToRemove) => {
    if (!window.confirm("Tem certeza que deseja excluir esta meta?")) return;
    clearMessages();
    const originalPdiMetas = pdiData.metas; const originalModalMetas = modalMetas;

    setPdiData(prev => ({ ...prev, metas: prev.metas.filter(meta => meta.id !== idToRemove), ultimaAtualizacao: new Date() }));
    setModalMetas(prev => prev.filter(meta => meta.id !== idToRemove));

    try {
        if (!userId) throw new Error("ID do usuário não encontrado.");
        const userPrivateDocRef = doc(db, 'usersprivado', userId);
        const metasFromDb = await getStandardizedDataFromDb(userPrivateDocRef, 'metas', standardizeMetaItem);
        const finalMetasParaDb = metasFromDb.filter(meta => meta.id !== idToRemove);
        await updateDoc(userPrivateDocRef, { "pdi.metas": finalMetasParaDb, "pdi.ultimaAtualizacao": new Date() });
        showSuccess("Meta removida com sucesso do PDI!");
    } catch (error) {
        console.error("Erro ao remover meta:", error); showError(`Erro ao remover meta: ${error.message}. Verifique permissões.`);
        setPdiData(prev => ({ ...prev, metas: originalPdiMetas })); setModalMetas(originalModalMetas);
    }
  };

  const adicionarFeedbackModal = async () => {
    if (!novoFeedbackText.trim() || !userId) { showError(!userId ? "ID do usuário não encontrado." : "O texto do feedback não pode estar vazio."); return; }
    clearMessages();
    const newFeedbackEntry = standardizeFeedbackItem({ texto: novoFeedbackText, autorId: userId, autorNome: userName || 'Usuário', data: new Date(), tipo: 'geral' });
    const originalPdiFeedbacks = pdiData.feedbackContinua; const originalModalFeedbacks = modalFeedbacks;

    setPdiData(prev => ({ ...prev, feedbackContinua: [newFeedbackEntry, ...originalPdiFeedbacks].sort((a, b) => b.data.getTime() - a.data.getTime()), ultimaAtualizacaoFeedbacks: new Date() }));
    setModalFeedbacks(prevModalFeedbacks => [newFeedbackEntry, ...prevModalFeedbacks].sort((a, b) => b.data.getTime() - a.data.getTime()));
    setNovoFeedbackText('');

    try {
      const userPrivateDocRef = doc(db, 'usersprivado', userId);
      const feedbacksFromDb = await getStandardizedDataFromDb(userPrivateDocRef, 'feedbackContinua', standardizeFeedbackItem);
      const finalFeedbacksForDb = [newFeedbackEntry, ...feedbacksFromDb].sort((a, b) => b.data.getTime() - a.data.getTime()).filter(fb => fb.data instanceof Date && !isNaN(fb.data.getTime()));
      await updateDoc(userPrivateDocRef, { "pdi.feedbackContinua": finalFeedbacksForDb, "pdi.ultimaAtualizacaoFeedbacks": new Date() });
      showSuccess("Feedback adicionado ao PDI!");
    } catch (error) {
      console.error("Erro ao adicionar feedback ao PDI: ", error); showError(`Erro ao adicionar feedback: ${error.message}. Verifique permissões.`);
      setPdiData(prev => ({ ...prev, feedbackContinua: originalPdiFeedbacks })); setModalFeedbacks(originalModalFeedbacks);
    }
  };

  const startEditFeedback = (feedback) => { setEditingFeedback(standardizeFeedbackItem({...feedback, originalTexto: feedback.texto})); clearMessages(); };
  const handleEditingFeedbackTextChange = (e) => { setEditingFeedback(prev => ({ ...prev, texto: e.target.value })); };

  const saveEditedFeedbackModal = async () => {
    if (!editingFeedback || !editingFeedback.texto.trim()) { showError("O texto do feedback não pode estar vazio."); return; }
    clearMessages();
    const originalPdiFeedbacks = pdiData.feedbackContinua; const originalModalFeedbacks = modalFeedbacks;
    const updatedFeedbackData = standardizeFeedbackItem({ ...editingFeedback, data: new Date() });

    setPdiData(prev => ({...prev, feedbackContinua: originalPdiFeedbacks.map(fb => fb.id === updatedFeedbackData.id ? updatedFeedbackData : fb).sort((a,b) => b.data.getTime() - a.data.getTime()), ultimaAtualizacaoFeedbacks: new Date()}));
    setModalFeedbacks(prevModalFeedbacks => prevModalFeedbacks.map(fb => fb.id === updatedFeedbackData.id ? updatedFeedbackData : fb).sort((a,b) => b.data.getTime() - a.data.getTime()));
    setEditingFeedback(null);

    try {
      const userPrivateDocRef = doc(db, 'usersprivado', userId);
      const feedbacksFromDb = await getStandardizedDataFromDb(userPrivateDocRef, 'feedbackContinua', standardizeFeedbackItem);
      const finalFeedbacksForDb = feedbacksFromDb.map(fb => fb.id === updatedFeedbackData.id ? updatedFeedbackData : fb).sort((a,b) => b.data.getTime() - a.data.getTime()).filter(fb => fb.data instanceof Date && !isNaN(fb.data.getTime()));
      await updateDoc(userPrivateDocRef, { "pdi.feedbackContinua": finalFeedbacksForDb, "pdi.ultimaAtualizacaoFeedbacks": new Date() });
      showSuccess("Feedback atualizado no PDI!");
    } catch (error) {
      console.error("Erro ao atualizar feedback no PDI: ", error); showError(`Erro ao atualizar feedback: ${error.message}. Verifique permissões.`);
      setPdiData(prev => ({...prev, feedbackContinua: originalPdiFeedbacks})); setModalFeedbacks(originalModalFeedbacks);
    }
  };

  const deleteFeedbackModal = async (feedbackIdToDelete) => {
    if (!window.confirm("Tem certeza que deseja excluir este feedback?")) return;
    clearMessages();
    const originalPdiFeedbacks = pdiData.feedbackContinua; const originalModalFeedbacks = modalFeedbacks;

    setPdiData(prev => ({ ...prev, feedbackContinua: originalPdiFeedbacks.filter(fb => fb.id !== feedbackIdToDelete), ultimaAtualizacaoFeedbacks: new Date() }));
    setModalFeedbacks(prevModalFeedbacks => prevModalFeedbacks.filter(fb => fb.id !== feedbackIdToDelete));

    try {
      const userPrivateDocRef = doc(db, 'usersprivado', userId);
      const feedbacksFromDb = await getStandardizedDataFromDb(userPrivateDocRef, 'feedbackContinua', standardizeFeedbackItem);
      const finalFeedbacksForDb = feedbacksFromDb.filter(fb => fb.id !== feedbackIdToDelete);
      await updateDoc(userPrivateDocRef, { "pdi.feedbackContinua": finalFeedbacksForDb, "pdi.ultimaAtualizacaoFeedbacks": new Date() });
      showSuccess("Feedback excluído do PDI!");
    } catch (error) {
      console.error("Erro ao excluir feedback do PDI: ", error); showError(`Erro ao excluir feedback: ${error.message}. Verifique permissões.`);
      setPdiData(prev => ({ ...prev, feedbackContinua: originalPdiFeedbacks })); setModalFeedbacks(originalModalFeedbacks);
    }
  };

  const indexOfLastMetaMain = currentPageMetasMain * METAS_PER_PAGE;
  const indexOfFirstMetaMain = indexOfLastMetaMain - METAS_PER_PAGE;
  const currentMetasMain = (Array.isArray(pdiData?.metas) ? pdiData.metas : []).slice(indexOfFirstMetaMain, indexOfLastMetaMain);
  const totalPagesMetasMain = Math.max(1, Math.ceil((Array.isArray(pdiData?.metas) ? pdiData.metas : []).length / METAS_PER_PAGE));

  const indexOfLastFeedbackModal = currentPageFeedbacksModal * FEEDBACKS_PER_PAGE;
  const indexOfFirstFeedbackModal = indexOfLastFeedbackModal - FEEDBACKS_PER_PAGE;
  const currentFeedbacksModal = (Array.isArray(modalFeedbacks) ? modalFeedbacks : []).slice(indexOfFirstFeedbackModal, indexOfLastFeedbackModal);
  const totalPagesFeedbacksModal = Math.max(1, Math.ceil((Array.isArray(modalFeedbacks) ? modalFeedbacks : []).length / FEEDBACKS_PER_PAGE));

  return (
    <div className="pdi-container">
      <p><strong>Objetivo Principal:</strong> {pdiData.objetivoPrincipal || 'Não definido.'}</p>

      <h4>Metas:</h4>
      {(!pdiData.metas || pdiData.metas.length === 0) ? (
        <p className="text-muted">Nenhuma meta definida.</p>
      ) : (
        <>
          <ul className="pdi-list-display">
            {currentMetasMain.map((meta) => {
              const displayStatus = getMetaDisplayStatus(meta);
              return (
                <li key={meta.id} className={`pdi-meta-item status-${displayStatus.classNameSuffix}`}>
                  <p><strong>{meta.descricao}</strong></p>
                  <p className="small text-muted">
                    Prazo: {formatDisplayDate(meta.prazo)} | Status: {displayStatus.text}
                  </p>
                  {meta.observacoes && <p className="small">Observações: {meta.observacoes}</p>}
                </li>
              );
            })}
          </ul>
          {totalPagesMetasMain > 1 && (
            <Pagination
              currentPage={currentPageMetasMain}
              totalPages={totalPagesMetasMain}
              onPageChange={setCurrentPageMetasMain}
            />
          )}
        </>
      )}

      <button onClick={openPDIModal} className="btn btn-primary btn-block submit-humor-btn mt-3">
        Gerenciar PDI (Metas e Feedbacks)
      </button>

      {isModalOpen && (
        <div className="pdi-modal-backdrop" onClick={closePDIModal}>
          <div className="pdi-modal-content" onClick={e => e.stopPropagation()}>
            <div className="pdi-modal-header">
              <h4>Gerenciar Plano de Desenvolvimento Individual</h4>
              <button onClick={closePDIModal} className="pdi-modal-close-btn">&times;</button>
            </div>
            <div className="pdi-modal-scrollable-content">
              <div className="pdi-modal-body">
                {(mensagem || erro) &&
                    <div className={`alert alert-${erro ? 'danger' : 'success'} feedback-message ${erro ? 'error' : 'success'}`}>
                        {erro || mensagem}
                    </div>
                }
                 <section>
                  <h5>Objetivo Principal do PDI (salva automaticamente)</h5>
                  <div className="form-group">
                    <label htmlFor="modalObjetivoPrincipal" className="form-label">Objetivo:</label>
                    <textarea
                        id="modalObjetivoPrincipal"
                        name="modalObjetivoPrincipal"
                        className="form-control"
                        value={modalObjetivoPrincipal}
                        onChange={(e) => setModalObjetivoPrincipal(e.target.value)}
                        rows="3"
                        placeholder="Defina seu principal objetivo de desenvolvimento..."
                    />
                  </div>
                </section>
                <hr />
                <section>
                  <h5>Metas (cada ação salva individualmente)</h5>
                  {editingMeta ? (
                    <div className="pdi-modal-meta-form edit-form">
                      <h6>Editando Meta: <span className="meta-desc-preview">{editingMeta.descricao.substring(0,30)}{editingMeta.descricao.length > 30 ? '...' : ''}</span></h6>
                      <div className="form-group"><label className="form-label">Descrição:</label><input type="text" name="descricao" className="form-control" value={editingMeta.descricao} onChange={handleEditingMetaChange} /></div>
                      <div className="form-group"><label className="form-label">Prazo:</label><input type="date" name="prazo" className="form-control" value={editingMeta.prazo} onChange={handleEditingMetaChange} /></div>
                      <div className="form-group">
                        <label className="form-label">Status:</label>
                        <select name="status" className="form-select" value={editingMeta.status} onChange={handleEditingMetaChange}>
                          <option value="Não Iniciado">Não Iniciado</option><option value="Em Andamento">Em Andamento</option><option value="Concluído">Concluído</option><option value="Bloqueado">Bloqueado</option>
                        </select>
                      </div>
                      <div className="form-group"><label className="form-label">Observações:</label><textarea name="observacoes" className="form-control" value={editingMeta.observacoes || ''} onChange={handleEditingMetaChange} rows="2" /></div>
                      <div className="pdi-modal-actions">
                        <button onClick={saveEditedMeta} className="btn btn-success btn-sm me-2">Confirmar Edição</button>
                        <button onClick={cancelEditMeta} className="btn btn-secondary btn-sm">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    modalMetas.length === 0 && <p className="text-muted text-center my-3">Nenhuma meta definida ainda.</p>
                  )}

                  {!editingMeta && modalMetas.length > 0 && (
                    <div className="pdi-modal-list">
                        {modalMetas.map((meta) => {
                        const displayStatus = getMetaDisplayStatus(meta);
                        return (
                            <div key={meta.id} className={`pdi-modal-list-item status-${displayStatus.classNameSuffix}`}>
                            <p><strong>{meta.descricao}</strong></p>
                            <p className="small text-muted">Prazo: {formatDisplayDate(meta.prazo)} | Status: {displayStatus.text}</p>
                            {meta.observacoes && <p className="small">Obs: {meta.observacoes}</p>}
                            <div className="actions">
                                <button onClick={() => startEditMeta(meta)} className="btn btn-sm btn-outline-primary">Editar</button>
                                <button onClick={() => removerMetaModal(meta.id)} className="btn btn-sm btn-outline-danger">Remover</button>
                            </div>
                            </div>
                        );
                        })}
                    </div>
                  )}

                  {!editingMeta && (
                    <div className="pdi-modal-meta-form add-form mt-3">
                      <h6>Adicionar Nova Meta</h6>
                      <div className="form-group"><label htmlFor="novaMetaDescricaoModal" className="form-label">Descrição:</label><input id="novaMetaDescricaoModal" type="text" name="descricao" className="form-control" value={novaMetaForm.descricao} placeholder="Descrição da Meta" onChange={handleNovaMetaFormChange} /></div>
                      <div className="form-group"><label htmlFor="novaMetaPrazoModal" className="form-label">Prazo:</label><input id="novaMetaPrazoModal" type="date" name="prazo" className="form-control" value={novaMetaForm.prazo} onChange={handleNovaMetaFormChange} /></div>
                      <div className="form-group">
                        <label htmlFor="novaMetaStatusModal" className="form-label">Status Inicial:</label>
                        <select id="novaMetaStatusModal" name="status" className="form-select" value={novaMetaForm.status} onChange={handleNovaMetaFormChange}>
                          <option value="Não Iniciado">Não Iniciado</option><option value="Em Andamento">Em Andamento</option>
                        </select>
                      </div>
                      <div className="form-group"><label htmlFor="novaMetaObsModal" className="form-label">Observações:</label><textarea id="novaMetaObsModal" name="observacoes" className="form-control" value={novaMetaForm.observacoes} placeholder="Observações (opcional)" onChange={handleNovaMetaFormChange} rows="2" /></div>
                      <button onClick={adicionarNovaMetaModal} className="btn btn-primary btn-block submit-humor-btn mt-3">Adicionar e Salvar Meta</button>
                    </div>
                  )}
                </section>
                <hr/>
                <section>
                  <h5>Feedbacks Contínuos (cada ação salva individualmente)</h5>
                  {editingFeedback ? (
                    <div className="pdi-modal-feedback-form edit-form">
                      <h6>Editando Feedback</h6>
                      <textarea className="form-control" value={editingFeedback.texto} onChange={handleEditingFeedbackTextChange} rows="3"/>
                      <div className="actions mt-2">
                        <button onClick={saveEditedFeedbackModal} className="btn btn-success btn-sm me-2">Salvar Edição</button>
                        <button onClick={() => setEditingFeedback(null)} className="btn btn-secondary btn-sm">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <div className="pdi-modal-feedback-form add-form">
                      <h6>Adicionar Novo Feedback</h6>
                      <div className="form-group">
                        <label htmlFor="novoFeedbackModal" className="form-label">Seu Feedback:</label>
                        <textarea id="novoFeedbackModal" className="form-control" value={novoFeedbackText} onChange={(e) => setNovoFeedbackText(e.target.value)} placeholder="Escreva seu feedback aqui..." rows="3"/>
                      </div>
                      <button onClick={adicionarFeedbackModal} className="btn btn-info btn-block submit-humor-btn mt-3">Enviar Feedback</button>
                    </div>
                  )}

                  <div className="pdi-modal-list mt-3">
                    {modalFeedbacks.length === 0 && <p className="text-muted text-center my-3">Nenhum feedback registrado ainda.</p>}
                    {currentFeedbacksModal.map((fb) => (
                      <div key={fb.id} className="pdi-modal-list-item">
                        <p><strong>{fb.autorNome || 'Autor desconhecido'}</strong> ({formatDisplayDate(fb.data)}):</p>
                        <p>{fb.texto}</p>
                        {(!editingFeedback || editingFeedback.id !== fb.id) && (
                          <div className="actions">
                            <button onClick={() => startEditFeedback(fb)} className="btn btn-sm btn-outline-primary">Editar</button>
                            <button onClick={() => deleteFeedbackModal(fb.id)} className="btn btn-sm btn-outline-danger">Excluir</button>
                          </div>
                        )}
                      </div>
                    ))}
                    {totalPagesFeedbacksModal > 1 && (
                        <Pagination
                            currentPage={currentPageFeedbacksModal}
                            totalPages={totalPagesFeedbacksModal}
                            onPageChange={setCurrentPageFeedbacksModal}
                        />
                    )}
                  </div>
                </section>
              </div>
            </div>
            <div className="pdi-modal-footer">
              <button onClick={closePDIModal} className="btn btn-secondary">Fechar Janela</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanoDesenvolvimentoIndividual;